import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import AspectRatio from 'components/utils/AspectRatio'
import { connect } from 'react-redux'
import { ImageParams } from 'components/Common/ResponsiveImage'
import PrimaryButtonPaletteProvider from 'contexts/PrimaryButtonPaletteProvider'
import PaletteProvider from 'contexts/PaletteProvider'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import HeroExperienceOfferDetails from './HeroExperienceOfferDetails'
import NestableClickable from 'components/Common/NestableClickable'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import config from 'constants/config'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { position, rem } from 'polished'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import noop from 'lib/function/noop'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import PartnershipLabel from 'components/Luxkit/Label/PartnershipLabel'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import { isMobile } from 'lib/web/deviceUtils'
import HeroOfferTileWrapper from './HeroOfferTileWrapper'

const MainImageSegment = styled.div`
  display: grid;
  position: relative;
`

interface MappedProps {
  isVelocityEnabled: boolean;
  isMobile: boolean;
}

interface Props {
  experience: App.ExperienceOffer;
  className?: string;
}

const imageParams: ImageParams = {
  mobileAspectRatio: '3:2',
  tabletAspectRatio: '16:9',
  desktopAspectRatio: '19:9',
  desktopWidth: '67vw',
  largeDesktopWidth: '750px',
  quality: 'good',
}

function HeroExperienceOfferTile(props: Props & MappedProps) {
  const {
    experience,
    className,
    isVelocityEnabled,
    isMobile,
  } = props

  const { images } = experience
  const fireOfferListEvent = useContext(OfferListEventsContext) ?? noop

  const handleClick = useCallback(() => {
    fireOfferListEvent(OfferListEvents.productClick, { experience, position, key: EventDataKey.ExperienceClick })
  }, [experience, fireOfferListEvent])

  const linkParams = useMemo(() => {
    return {
      onClick: handleClick,
      to: getExperienceOfferPageURL(experience),
      'data-testid': 'offer-experience',
      ...(config.OPEN_NEW_TAB_OFFER_CLICK ? { target: '_blank' } : {}),
    }
  }, [handleClick, experience])

  const OfferOverlayContainer = styled(LabelGroup)`
    position: absolute;
    top: ${rem(12)};
    left: ${rem(12)};
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`

  return (
    <NestableClickable {...linkParams} className={className} target="_blank">
      <PrimaryButtonPaletteProvider palette="default">
        <HeroOfferTileWrapper>
          <MainImageSegment>
            <AspectRatio mobileAspectRatio="3:2" tabletAspectRatio="16:9" desktopAspectRatio="19:9" fillHeight>
              {isMobile &&
                <HeightFillingImage
                  id={images[0].id ?? ''}
                  {...imageParams}
                />
              }
              {!isMobile &&
                <ImageCarousel
                  images={images}
                  imageParams={imageParams}
                />
              }
              {isVelocityEnabled && (
                <OfferOverlayContainer>
                  <PartnershipLabel canEarn canUse variant="icon"/>
                </OfferOverlayContainer>
              )}
            </AspectRatio>
            <TripGuard>
              <CSSBreakpoint max="tablet">
                <PaletteProvider type="default">
                  <FloatingBookmarkContainer>
                    <ExperienceBookmarkButton offer={experience} />
                  </FloatingBookmarkContainer>
                </PaletteProvider>
              </CSSBreakpoint>
            </TripGuard>
          </MainImageSegment>
          <CSSBreakpoint max="tablet">
            <HeroExperienceOfferDetails experience={experience} isDesktop={false}/>
          </CSSBreakpoint>
          <CSSBreakpoint min="desktop">
            <HeroExperienceOfferDetails experience={experience} isDesktop />
          </CSSBreakpoint>
        </HeroOfferTileWrapper>
      </PrimaryButtonPaletteProvider>
    </NestableClickable>
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  isVelocityEnabled: state.geo.partnership?.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE,
  isMobile: isMobile(state.config.userAgent.osName),
})

export default connect(mapStateToProps)(HeroExperienceOfferTile)
